/**
 * KPI Builder Modal (Phase 24.1, extended Phase 24.7 / Track 1)
 *
 * AI-guided conversation for generating custom KPI definitions.
 * Stateless: sends full message history with each request.
 * After conversation completes, shows preview card + Scope panel (Phase 24.7).
 *
 * Phase 24.7 (Track 1): zone scope is captured AFTER the AI conversation by the
 * trainer (system-prompt guardrail in Chunk 3 stops Gemini from asking about
 * zones). The "Save KPI" button stays disabled until at least one zone is
 * picked — mirrors the Zod `stationIds.min(1)` constraint server-side.
 */

import { useState, useRef, useEffect } from 'react';
import {
  X,
  Send,
  Loader2,
  Bot,
  User,
  Gauge,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  MapPin,
} from 'lucide-react';
import { useKpiBuilderConversation, useGenerateKpi } from '../../hooks/useKpi';
import { useStationLookup } from '../../hooks/useStations';
import { usePositionLookup } from '../../hooks/usePositions';
import { ScopeChipPicker } from '../../components/kpi/ScopeChipPicker';
import type { KpiCategory } from '@guild/shared';
import { categoryConfig } from '../../lib/kpi-categories';

interface Message {
  role: 'ai' | 'user';
  text: string;
}

interface KpiBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function KpiBuilderModal({ isOpen, onClose, onCreated }: KpiBuilderModalProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [kpiResult, setKpiResult] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Phase 24.7 (Track 1): scope captured post-conversation. Required for Save.
  const [scopeStationIds, setScopeStationIds] = useState<string[]>([]);
  const [scopePositionCodes, setScopePositionCodes] = useState<string[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);

  const conversationMutation = useKpiBuilderConversation();
  const generateMutation = useGenerateKpi();

  // Lookups for the scope panel — only fetched when modal is open.
  const { data: stationLookup, isLoading: stationsLoading } = useStationLookup();
  const { data: positionLookup, isLoading: positionsLoading } = usePositionLookup();

  // Auto-scroll to bottom
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Start conversation on mount
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      startConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const startConversation = async () => {
    setError(null);
    setKpiResult(null);
    try {
      const result = await conversationMutation.mutateAsync({ messages: [] });
      if (result.question) {
        setMessages([{ role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([{ role: 'ai', text: 'Sorry, I had trouble starting the conversation. Please try again.' }]);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || conversationMutation.isPending) return;

    const userMessage: Message = { role: 'user', text: input.trim() };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setError(null);

    try {
      const result = await conversationMutation.mutateAsync({ messages: updatedMessages });

      if (result.done && result.kpiDefinition) {
        setKpiResult(result.kpiDefinition);
        setMessages([...updatedMessages, {
          role: 'ai',
          text: `I've generated a KPI definition: "${result.kpiDefinition.name as string}". Pick the zones it should run on, then click "Save KPI".`,
        }]);
      } else if (result.question) {
        setMessages([...updatedMessages, { role: 'ai', text: result.question }]);
      }
    } catch {
      setMessages([...updatedMessages, {
        role: 'ai',
        text: 'Sorry, I had trouble processing your response. Please try again.',
      }]);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleAccept = async () => {
    if (!kpiResult) return;
    // Phase 24.7 (Track 1): block save when no zones picked. Defense-in-depth —
    // the button is also `disabled` below, but this guard handles edge cases
    // (e.g., Enter-to-submit when the button is enabled then state changes).
    if (scopeStationIds.length === 0) {
      setError('KPIs must have at least one zone');
      return;
    }
    setError(null);

    try {
      await generateMutation.mutateAsync({
        ...kpiResult,
        stationIds: scopeStationIds,
        // Empty array → omit so backend interprets as "all positions" (Q4 default).
        positionCodes: scopePositionCodes.length > 0 ? scopePositionCodes : undefined,
      });
      onCreated();
      handleReset();
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save KPI';
      setError(message);
    }
  };

  const handleRegenerate = () => {
    handleReset();
    startConversation();
  };

  const handleReset = () => {
    setMessages([]);
    setInput('');
    setKpiResult(null);
    setError(null);
    setScopeStationIds([]);
    setScopePositionCodes([]);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  const category = kpiResult?.category as KpiCategory | undefined;
  const categoryInfo = category ? categoryConfig[category] : null;
  const thresholds = kpiResult?.defaultThresholds as { warning?: number; critical?: number; target?: number } | null;

  // ScopeChipPicker option arrays (lookup endpoints already filter isActive=true).
  const stationOptions = (stationLookup ?? []).map((s) => ({ id: s.id, name: s.name }));
  // Position picker uses code (not id) since positionCodes is a string[] in the schema.
  const positionOptions = (positionLookup ?? []).map((p) => ({ id: p.code, name: p.name }));

  const canSave = scopeStationIds.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl mx-4 bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-guild-mint/50 flex items-center justify-center">
              <Gauge size={18} className="text-guild-green" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Create KPI with AI</h2>
              <p className="text-xs text-gray-500">
                {kpiResult
                  ? 'Review the generated KPI and pick its scope'
                  : 'Answer a few questions about what you want to measure'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Chat Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-h-[250px]">
          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}>
              {msg.role === 'ai' && (
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
                  <Bot size={14} className="text-guild-green" />
                </div>
              )}
              <div
                className={`
                  max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed
                  ${msg.role === 'ai'
                    ? 'bg-gray-50 text-gray-800 rounded-tl-md'
                    : 'bg-guild-green text-white rounded-tr-md'
                  }
                `}
              >
                {msg.text}
              </div>
              {msg.role === 'user' && (
                <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-green/10 flex items-center justify-center">
                  <User size={14} className="text-guild-green" />
                </div>
              )}
            </div>
          ))}

          {/* Typing indicator */}
          {conversationMutation.isPending && messages.length > 0 && messages[messages.length - 1].role === 'user' && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 h-7 w-7 rounded-full bg-guild-mint/50 flex items-center justify-center">
                <Bot size={14} className="text-guild-green" />
              </div>
              <div className="bg-gray-50 rounded-2xl rounded-tl-md px-4 py-3">
                <div className="flex gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full bg-gray-300 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* KPI Preview + Scope Capture (Phase 24.7) */}
        {kpiResult && (
          <div className="px-6 py-4 border-t border-gray-100 bg-gray-50/50 space-y-4 overflow-y-auto">
            {/* Preview card */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">{kpiResult.name as string}</h3>
                  <p className="text-xs text-gray-500 font-mono">{kpiResult.code as string}</p>
                </div>
                {categoryInfo && (
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${categoryInfo.bg} ${categoryInfo.text}`}>
                    <categoryInfo.icon size={12} />
                    {categoryInfo.label}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600">{kpiResult.description as string}</p>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-400">Unit</span>
                  <p className="font-medium text-gray-700">{(kpiResult.unit as string) || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-400">Format</span>
                  <p className="font-medium text-gray-700">{(kpiResult.displayFormat as string) || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <span className="text-gray-400">Aggregation</span>
                  <p className="font-medium text-gray-700">{(kpiResult.aggregationType as string) || 'N/A'}</p>
                </div>
              </div>
              {thresholds && (
                <div className="flex gap-4 text-xs">
                  {thresholds.target != null && (
                    <span className="text-emerald-600">Target: {thresholds.target}</span>
                  )}
                  {thresholds.warning != null && (
                    <span className="text-amber-600">Warning: {thresholds.warning}</span>
                  )}
                  {thresholds.critical != null && (
                    <span className="text-red-600">Critical: {thresholds.critical}</span>
                  )}
                </div>
              )}
            </div>

            {/* Scope panel — Phase 24.7 (Track 1) */}
            <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-guild-green" />
                <h4 className="text-sm font-bold text-gray-900">Scope</h4>
                <span className="text-xs text-gray-500">— where this KPI will run</span>
              </div>

              <ScopeChipPicker
                label="Zones"
                options={stationOptions}
                selectedIds={scopeStationIds}
                onChange={setScopeStationIds}
                required
                placeholder={stationsLoading ? 'Loading…' : 'Select zones'}
                helpText="Required. KPI runs on assessments in these zones."
                errorMessage="KPIs must have at least one zone"
                disabled={stationsLoading || generateMutation.isPending}
              />

              <ScopeChipPicker
                label="Positions"
                options={positionOptions}
                selectedIds={scopePositionCodes}
                onChange={setScopePositionCodes}
                placeholder={positionsLoading ? 'Loading…' : 'Select positions'}
                helpText="Optional. Empty = all positions."
                disabled={positionsLoading || generateMutation.isPending}
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            {/* Save / Regenerate actions */}
            <div className="flex gap-3">
              <button
                onClick={handleAccept}
                disabled={generateMutation.isPending || !canSave}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl bg-guild-green px-4 py-2.5 text-sm font-semibold text-white hover:bg-guild-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {generateMutation.isPending ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle2 size={16} />
                )}
                Save KPI
              </button>
              <button
                onClick={handleRegenerate}
                disabled={generateMutation.isPending}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-all"
              >
                <RefreshCw size={16} />
                Regenerate
              </button>
            </div>
          </div>
        )}

        {/* Input area (only when conversation is ongoing) */}
        {!kpiResult && (
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your answer..."
                disabled={conversationMutation.isPending}
                className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-guild-green focus:ring-1 focus:ring-guild-green/30 outline-none disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || conversationMutation.isPending}
                className="rounded-xl bg-guild-green px-4 py-2.5 text-white hover:bg-guild-green/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
